<template>
  <div v-if="pageRendered">
    <div class="row" ref="editor">
      <template v-if="editing">
        <div class="col">
          <DatePropertiesToInclude @configChanged="updateConfig($event)"
                                   :currentConfig="config"></DatePropertiesToInclude>
        </div>
        <div v-if="getAmountOfPropertiesSelected > 1" class="col-4">
          <label>{{ $t('insights.ui.datePropertyChartType.question') }}</label>
          <div class="custom-control custom-radio">
            <input type="radio" :id="'property-stacked-'+ this.uniqueId" name="property-stacked"
                   value="stacked"
                   @change="setSumType($event)"
                   class="custom-control-input" v-model="tempConfig.datePropertyChartType">
            <label class="custom-control-label"
                   :for="'property-stacked-'+ this.uniqueId">{{
                $t('insights.ui.datePropertyChartType.answers.stacked')
              }}</label>
          </div>
          <div class="custom-control custom-radio">
            <input type="radio" :id="'property-colored-'+ this.uniqueId" name="property-colored"
                   value="colored"
                   @change="setSumType($event)"
                   class="custom-control-input" v-model="tempConfig.datePropertyChartType">
            <label class="custom-control-label"
                   :for="'property-colored-'+ this.uniqueId">{{
                $t('insights.ui.datePropertyChartType.answers.colored')
              }}</label>
          </div>
        </div>
        <DaySelect :history="true" :today="true" :future="false" @configChanged="updateConfig($event)"
                   :currentConfig="config"/>
        <div class="col-3">
          <IncludeWeekends @configChanged="updateConfig($event)" :currentConfig="config"></IncludeWeekends>
        </div>
        <div class="col-12">
          <label for="title-properties">{{ $t('ui.title') }}</label>
          <input type="text" id="title-properties" class="form-control" v-model="config.title">
        </div>
      </template>
    </div>
    <div class="row" v-if="!editing">
        <div class="col-12 chart-container">
          <div class="real-chart-container">
            <component-load-spinner :loading="loading"></component-load-spinner>
            <bar-chart
                style="width: 100%; height: 100%"
                :style="loading ? 'opacity:0.5' : 'opacity:1'"
                :class="loading ? 'loading':''"
                :chartData="getChartData"
                :options="{ ...getOptions }"/>
          </div>
        </div>
    </div>
  </div>
</template>

<script>
import {mapGetters} from "vuex";
import generalMethods from "@/mixins/insights/generalMethods";
import DatePropertiesToInclude from "@/components/insights/forms/DatePropertiesToInclude";
import DaySelect from "@/components/insights/forms/DaySelect";
import timespan from "@/mixins/timespan";
import BarChart from "@/components/insights/charts/barChart";
import ComponentLoadSpinner from "@/components/general/ComponentLoadSpinner";
import IncludeWeekends from "@/components/insights/forms/IncludeWeekends";
import {stringToColor} from "@/mixins/ui/stringToColor";

export default {
  components: {
    DatePropertiesToInclude,
    DaySelect,
    BarChart,
    ComponentLoadSpinner,
    IncludeWeekends
  },
  mixins: [generalMethods, timespan, stringToColor],
  props: ['editing'],
  data() {
    return {
      uniqueId: _.uniqueId(),
      fsQueries: {},
      currentBounds: [],
      amount: 0,
      config: { datePropertiesToCount: {}, title: '', datePropertyChartType: 'sumAll' },
      loading: true,
      pageRendered: false,
      tempConfig: {
        propertiesToInclude: [],
        datePropertyChartType: 'stacked'
      }
    }
  },
  firestore() {
    return {}
  },
  computed: {
    getChartData() {
      const datasets = [];
      if (this.config.componentConfig.datePropertyChartType === 'stacked') {
        let totals = _.map(this.getDatesOfChart, () => { return 0 });
        _.forEach(this.config.componentConfig.datePropertiesToCount, (count, property) => {
          if (count) {
            for(let i = 0; i <this.getDatesOfChart.length; i++) {
              const date = this.getDatesOfChart[i];
              const amountOnDate = _.filter(this.fsQueries['fsQuery' + property], job => {
                return job[property] ? moment(job[property].toDate()).isSame(date, 'date') : false;
              }).length;
              totals[i] += amountOnDate;
            }
          }
        });
        datasets.push({
          stack: this.$t('insights.ui.totalAmount'),
          label: this.$t('insights.ui.totalAmount'),
          backgroundColor: this.stringToShiftColor('totalAmount'),
          data: totals
        })
      } else {
        _.forEach(this.config.componentConfig.datePropertiesToCount, (count, property) => {
          if (count) {
            datasets.push({
              stack: this.$t('insights.ui.dateProperty.' + property),
              label: this.$t('insights.ui.dateProperty.' + property),
              backgroundColor: this.stringToShiftColor('fsQuery' + property),
              data: _.map(this.getDatesOfChart, date => {
                return _.filter(this.fsQueries['fsQuery' + property], job => {
                  return job[property] ? moment(job[property].toDate()).isSame(date, 'date') : false;
                }).length
              }),
            })
          }
        });
      }
      return {
        labels: this.getLabels,
        datasets
      }
    },
    getDatesOfChart() {
      if (!this.config.dayDifference) {
        return []
      }
      return this.getDatesWithinRangeFromToday(this.config.dayDifference.start, this.config.dayDifference.end - this.config.dayDifference.start, this.config.includeWeekends, this.config.includeDutchHolidays);
    },
    getLabels() {
      const dates = this.getDatesOfChart;
      return _.map(dates, date => {
        return moment(date).format('DD-MM-YYYY')
      });
    },
    getOptions() {
      const self = this
      let config = {
        ...this.generalChartOptions,
        animation: {
          onComplete: () => {

            self.loading = false;
          }
        },
        scales: {
          xAxes: [
            {
              gridLines: {
                display: false
              },
              // bounds: 'ticks',
              offset: true, // IF this is set to false, it will not display the last bar!
              stacked: true,
              autoSkip: false,
              startAt: 0,
            },
          ],
          yAxes: [{
            ticks: {
              beginAtZero: true,
              autoSkip: true,
              type: 'linear',
            },
          }],
        },
        tooltips: {

        },
        plugins: {
          datalabels: {
            color: 'black',
            anchor: 'start',
            align: 'bottom',
            font: {
              // weight: 'bold',
              size: 20
            },
            offset: -50,

            display: function (ctx) {
              return false
              return ctx.datasetIndex > 0 //ctx.chart.$totalizer.utmost;
            },
            formatter: (value, ctx) => {
              return value;
            },
          }
        },
        responsive: true,
        maintainAspectRatio: false,
      }
      config.layout.padding.bottom = 20;

      return config;
    },

    ...mapGetters('company', ['getConfig']),
    ...mapGetters('users', ['getClaims']),
    getPropertiesToCount() {
      return this.getConfig.datePropertiesToCount;
    },
    getAmountOfPropertiesSelected() {
      if (this.config.componentConfig && this.config.componentConfig.datePropertiesToCount) {
        return _.size(_.filter(_.toArray(this.config.componentConfig.datePropertiesToCount), b => !!b))
      } else {
        return 0;
      }
    },
    getBoundProperties() {
      return _.map(this.currentBounds, bound => {
        return this.fsQueries[bound]
      })
    },
    getAmount() {
      return this.calculateAmount(this.getBoundProperties);
    }
  },
  methods: {
    calculateAmount(boundProperties) {
      let jobIds = [];
      _.forEach(boundProperties, bound => {
        jobIds = jobIds.concat(_.map(bound, 'id'))
      })

      if (this.config.componentConfig.datePropertyChartType === 'stacked') {
        return _.size(jobIds);
      } else {
        return _.size(_.uniq(jobIds))
      }
    },
    setSumType(event) {
      this.config.componentConfig.datePropertyChartType = event.srcElement.value;
      this.emitConfig();
    },
    updateConfig(newConfig) {
      this.config = newConfig;
      this.$emit('configChanged', this.config)
    },
    updateFirestore() {
      _.forEach(this.currentBounds, currentBound => {
        this.$unbind('fsQueries. ' + currentBound);
      });
      this.currentBounds = [];
      if (this.config.componentConfig) {
        const initialQueries = {};
        _.forEach(this.config.componentConfig.datePropertiesToCount, (count, property) => {
          initialQueries['fsQuery' + property] = [];
        });
        this.fsQueries = initialQueries;
        _.forEach(this.config.componentConfig.datePropertiesToCount, (count, property) => {
          if (count) {
            let userClaims = this.getClaims;
            this.$bind('fsQueries.fsQuery' + property, this.$FB.collection('jobs')
                .where('companyId', '==', userClaims.companyId)
                .where(property, '>=', moment(_.min(this.getDatesOfChart)).startOf('date').toDate())
                .where(property, '<=', moment(_.max(this.getDatesOfChart)).endOf('date').toDate()));

            this.currentBounds.push('fsQuery' + property)
          }
        });
      } else {
        this.fsQueries = {};
      }
    },
  },
  created() {
    setTimeout(() => {
      if (!this.config.componentConfig) {
        this.config.componentConfig = {datePropertyChartType: 'stacked'};
      }
      this.updateFirestore();
      this.pageRendered = true;
    }, 500);
  },
  watch: {
    'config'(newValue, oldValue) {
      console.warn("newValue, oldValue", newValue, oldValue)
      if (!_.isEqual(_.omit(newValue, ['title']), _.omit(oldValue, ['title'])) && this.pageRendered) {
        this.updateFirestore();
      }
    },
  }
}
</script>

<style scoped lang="scss">
@import "./src/assets/scss/components/insights/_generalStylingCharts.scss";

.amount {
  font-size: 5rem;
  font-weight: bold;
}

.chart-custom-date {
  text-align: center;
}

.real-chart-container {

}
</style>
